import { FC } from 'react';

import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';

import CustomTickWithBackground from '@/components/organisms/EditCell/components/EditCellChartRenderer/components/CustomTickWithBackground';
import { CHARTS_TEST_IDS } from '@/constants/data-testids';

import { Card, CardContent } from '../../atoms/Card/Card';

type DistributionChartProps = {
  title: string;
  xKey: string;
  yKey: string;
  data: {
    name: string;
    value: number;
    trend?: number;
    color?: string;
  }[];
  trendLineData: number[];
  isEditCellSkeleton?: boolean;
};

const DEFAULT_LINE_COLOR = '#B3E5D2';

const DEBOUNCE_TIME = 50;

const CONTAINER_HEIGHT = 284;

const DOMAIN = [0, 100];

export const DistributionChart: FC<DistributionChartProps> = ({
  title,
  data,
  xKey,
  yKey,
  trendLineData,
  isEditCellSkeleton = false,
}) => {
  return (
    <Card
      data-testid={CHARTS_TEST_IDS.DISTRIBUTION_CHART}
      className="p-4 border-custom-warm-grey-150"
    >
      <CardContent>
        <div>
          <p className="text-body-14-semibold !text-custom-warm-grey-700 mb-6">{title}</p>
        </div>
        <ResponsiveContainer width="100%" height={CONTAINER_HEIGHT} debounce={DEBOUNCE_TIME}>
          <ComposedChart
            margin={{
              bottom: 26,
            }}
            data={data}
          >
            <CartesianGrid stroke="#F0EEED" strokeDasharray="12 12" vertical={false} />

            <XAxis
              dataKey={'value'}
              type="category"
              axisLine={false}
              tickLine={false}
              domain={DOMAIN}
              dy={12}
              tick={
                isEditCellSkeleton ? (
                  <CustomTickWithBackground />
                ) : (
                  {
                    fontSize: 12,
                    letterSpacing: '-0.01em',
                    color: '#A8A29D',
                  }
                )
              }
              hide
            />
            <XAxis
              xAxisId={1}
              dataKey="xCoord"
              type="number"
              axisLine={false}
              tickLine={false}
              domain={DOMAIN}
              dy={12}
              tick={
                isEditCellSkeleton ? (
                  <CustomTickWithBackground />
                ) : (
                  {
                    fontSize: 12,
                    letterSpacing: '-0.01em',
                    color: '#A8A29D',
                  }
                )
              }
              hide
            />

            <YAxis
              type="number"
              axisLine={false}
              tickLine={false}
              tick={
                isEditCellSkeleton ? (
                  <CustomTickWithBackground rectWidth={24} rectHeight={12} rectX={-24} rectY={-6} />
                ) : (
                  {
                    fontSize: 12,
                    color: '#79716B',
                  }
                )
              }
              domain={DOMAIN}
            />
            <Bar dataKey={yKey} barSize={80} radius={[6, 6, 0, 0]}>
              {data.map(d => (
                <Cell key={d[xKey as 'name']} fill={d.color ?? DEFAULT_LINE_COLOR} />
              ))}
              {!isEditCellSkeleton && (
                <LabelList
                  dataKey={yKey}
                  position="bottom"
                  offset={-22}
                  fill="#44403C"
                  fontSize={14}
                />
              )}
              <LabelList
                dataKey="name"
                content={({ x, width, value }) =>
                  isEditCellSkeleton ? (
                    <CustomTickWithBackground
                      x={Number(x) + Number(width) / 2 - 20}
                      y={CONTAINER_HEIGHT - 15}
                      rectWidth={Number(width)}
                      rectHeight={12}
                    />
                  ) : (
                    <text
                      x={Number(x) + Number(width) / 2}
                      y={CONTAINER_HEIGHT - 5}
                      textAnchor="middle"
                      fill="#79716B"
                      fontSize={12}
                    >
                      {value}
                    </text>
                  )
                }
              />
            </Bar>

            <Line
              xAxisId={1}
              type="monotone"
              dataKey="value"
              stroke="#50AE9C"
              strokeWidth={3}
              dot={false}
              activeDot={false}
              data={trendLineData.map((value, index) => ({
                xCoord: (index * 100) / (trendLineData.length - 1),
                value,
              }))}
              tooltipType="none"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};
